/**
 * Clarinet type
 */

export function getClarinetTypes() {
  return ["bFlat", "bass"];
}

export function isDisabledClarinetType(clarinetType) {
  const disabledClarinetTypes = [];
  return !!~disabledClarinetTypes.indexOf(clarinetType);
}

export function isValidClarinetType(clarinetType) {
  const options = getClarinetTypes();
  return (
    !!~options.indexOf(clarinetType) && !isDisabledClarinetType(clarinetType)
  );
}

/**
 * Baffle
 */

export function getBaffleTypes(clarinetType) {
  switch (clarinetType) {
    case "bFlat":
      return ["D", "B", "F"];

    case "bass":
      return ["D", "B", "F"];

    default:
      return [];
  }
}

export function isDisabledBaffleType(baffleType, clarinetType) {
  let disabledBaffleTypes = [];

  switch (clarinetType) {
    case "bFlat":
      break;

    case "bass":
      break;

    default:
      return true;
  }

  return !!~disabledBaffleTypes.indexOf(baffleType);
}

export function isValidBaffleType(baffleType, clarinetType) {
  const options = getBaffleTypes(clarinetType);
  return (
    !!~options.indexOf(baffleType) &&
    !isDisabledBaffleType(baffleType, clarinetType)
  );
}

export function getDefaultBaffleType(clarinetType) {
  switch (clarinetType) {
    case "bFlat":
    case "bass":
      return "D";

    default:
      return null;
  }
}

export function isValidBaffleParam(baffleParam) {
  return parseFloat(baffleParam) === baffleParam;
}

export function isValidBaffleParams(baffleParams, baffleType, clarinetType) {
  if (!Array.isArray(baffleParams)) return false;
  const requiredParamsLength = getDefaultBaffleParams(
    baffleType,
    clarinetType
  ).length;

  if (requiredParamsLength !== baffleParams.length) return false;

  return (
    baffleParams.filter((param) => !isValidBaffleParam(param)).length === 0
  );
}

// eslint-disable-next-line no-unused-vars
export function getDefaultBaffleParams(baffleType, clarinetType) {
  switch (baffleType) {
    case "B":
    case "F":
      return [0, 0];

    case "D":
      return [];

    default:
      return [];
  }
}

/**
 * Chamber
 */

export function getChamberTypes(clarinetType) {
  switch (clarinetType) {
    case "bFlat":
      return ["Ch", "H", "R", "C"];

    case "bass":
      return ["Ch", "H", "R", "C"];

    default:
      return [];
  }
}

export function isDisabledChamberType(chamberType, clarinetType) {
  let disabledChamberTypes = [];

  switch (clarinetType) {
    case "bFlat":
      disabledChamberTypes = [];
      break;

    case "bass":
      disabledChamberTypes = [];
      break;

    default:
      return true;
  }

  return !!~disabledChamberTypes.indexOf(chamberType);
}

export function isValidChamberType(chamberType, clarinetType) {
  const options = getChamberTypes(clarinetType);
  return (
    !!~options.indexOf(chamberType) &&
    !isDisabledChamberType(chamberType, clarinetType)
  );
}

export function getDefaultChamberType() {
  return "Ch";
}

export function getChamberParamsRanges(chamberType, clarinetType) {
  const defaultRanges = [
    [0, Infinity],
    [0, Infinity],
  ];

  switch (clarinetType) {
    case "bFlat":
      // prettier-ignore
      switch (chamberType) {
        case "Ch": return [[6, 14], [6, 14]];
        case "H": return [[6, 14]];
        case "R":  return [[4, 14.8]];
        case "C":  return [[4, 14]];
        default:   return defaultRanges;
      }

    case "bass":
      // prettier-ignore
      switch (chamberType) {
        case "Ch": return [[6, 23], [6, 23]];
        case "H":  return [[6, 23]];
        case "R":  return [[4, 24]];
        case "C":  return [[4, 23]];
        default:   return defaultRanges;
      }

    default:
      return defaultRanges;
  }
}

export function isValidChamberParam(chamberParam, range) {
  const min = range[0];
  const max = range[1];

  return (
    parseFloat(chamberParam) === chamberParam &&
    chamberParam >= min &&
    chamberParam <= max
  );
}

export function isValidChamberParams(chamberParams, chamberType, clarinetType) {
  if (!Array.isArray(chamberParams)) return false;
  const requiredParamsLength = getDefaultChamberParams(
    chamberType,
    clarinetType
  ).length;

  if (requiredParamsLength !== chamberParams.length) return false;

  const ranges = getChamberParamsRanges(chamberType, clarinetType);

  return (
    chamberParams.filter(
      (param, idx) => !isValidChamberParam(param, ranges[idx])
    ).length === 0
  );
}

// eslint-disable-next-line no-unused-vars
export function getDefaultChamberParams(chamberType, clarinetType) {
  switch (chamberType) {
    case "Ch":
      return [0, 0];

    case "H":
    case "R":
    case "C":
      return [0];

    default:
      return [];
  }
}

/**
 * Striations
 */

// eslint-disable-next-line no-unused-vars
export function isValidStriations(baffleStriations, baffleType, clarinetType) {
  if (parseInt(baffleStriations, 10) !== baffleStriations) {
    return false;
  }

  return baffleStriations === 0;
}

export function getDefaultStriations() {
  return 0;
}

/**
 * Tip opening
 */

export function getTipOpeningRange(clarinetType) {
  switch (clarinetType) {
    case "bFlat":
      return [90, 300];

    case "bass":
      return [150, 300];

    default:
      return [0, Infinity];
  }
}

export function isValidTipOpening(tipOpening, clarinetType) {
  const range = getTipOpeningRange(clarinetType);
  const min = range[0];
  const max = range[1];

  return (
    parseInt(tipOpening, 10) === tipOpening &&
    tipOpening >= min &&
    tipOpening <= max
  );
}

/**
 * Facing
 */

export function getFacingRange(clarinetType) {
  switch (clarinetType) {
    case "bFlat":
      return [16, 26];

    case "bass":
      return [24, 32];

    default:
      return [0, Infinity];
  }
}

export function isValidFacing(facing, clarinetType) {
  const range = getFacingRange(clarinetType);
  const min = range[0];
  const max = range[1];

  return parseInt(facing, 10) === facing && facing >= min && facing <= max;
}

export function getDefaultFacing(clarinetType) {
  // prettier-ignore
  switch (clarinetType) {
    case "bFlat": return 20;
    case "bass":  return 28;

    default:
      return null;
  }
}

/**
 * Bore diameter
 */

export function getBoreDiameterRange(clarinetType) {
  switch (clarinetType) {
    case "bFlat":
      return [11, 16];

    case "bass":
      return [16, 25];

    default:
      return [0, Infinity];
  }
}

export function isValidBoreDiameter(boreDiameter, clarinetType) {
  const range = getBoreDiameterRange(clarinetType);
  const min = range[0];
  const max = range[1];

  return (
    parseFloat(boreDiameter) === boreDiameter &&
    boreDiameter >= min &&
    boreDiameter <= max
  );
}

export function getDefaultBoreDiameter(clarinetType) {
  // prettier-ignore
  switch (clarinetType) {
    case "bFlat": return 14.8;
    case "bass":  return 24;

    default:
      return null;
  }
}

/**
 * Mouthpiece
 */

export function getMouthpieceErrors(mouthpiece = {}) {
  const clarinetType = mouthpiece.clarinetType;
  const baffle = mouthpiece.baffle || {};
  const chamber = mouthpiece.chamber || {};
  const striations = mouthpiece.striations;
  const tipOpening = mouthpiece.tipOpening;
  const facing = mouthpiece.facing;
  const boreDiameter = mouthpiece.boreDiameter;

  const errors = {};

  if (!isValidClarinetType(clarinetType))
    errors["clarinetType"] = `Invalid clarinet type: ${clarinetType}`;

  if (!isValidBaffleType(baffle.type, clarinetType))
    errors["baffle.type"] = `Invalid baffle type: ${baffle.type}`;

  if (!isValidBaffleParams(baffle.params, baffle.type, clarinetType))
    errors["baffle.params"] = `Invalid baffle params: ${baffle.params}`;

  if (!isValidChamberType(chamber.type, clarinetType))
    errors["chamber.type"] = `Invalid chamber type: ${chamber.type}`;

  if (!isValidChamberParams(chamber.params, chamber.type, clarinetType))
    errors["chamber.params"] = `Invalid chamber params: ${chamber.params}`;

  if (!isValidStriations(striations, baffle.type, clarinetType))
    errors["striations"] = `Invalid striations: ${striations}`;

  if (!isValidTipOpening(tipOpening, clarinetType))
    errors["tipOpening"] = `Invalid tip opening: ${tipOpening}`;

  if (!isValidFacing(facing)) errors["facing"] = `Invalid facing: ${facing}`;

  if (!isValidBoreDiameter(boreDiameter, clarinetType))
    errors["boreDiameter"] = `Invalid shank diameter: ${boreDiameter}`;

  return Object.keys(errors).length ? errors : undefined;
}

export function validateMouthpiece(mouthpiece) {
  const errors = getMouthpieceErrors(mouthpiece);
  if (!errors) return;

  const message = Object.values(errors).join("\n");
  throw new Error(message);
}

export function isValidMouthpiece(mouthpiece) {
  try {
    validateMouthpiece(mouthpiece);
    return true;
  } catch (err) {
    return false;
  }
}

export function applyMouthpieceDefaults(mouthpiece = {}) {
  const clarinetType = mouthpiece.clarinetType;

  if (!isValidClarinetType(clarinetType)) return mouthpiece;

  const result = Object.assign({}, mouthpiece);

  result.baffle = result.baffle || {};
  result.chamber = result.chamber || {};

  // Baffle type
  result.baffle.type =
    result.baffle.type != null
      ? result.baffle.type
      : getDefaultBaffleType(clarinetType);

  // Baffle params
  result.baffle.params =
    result.baffle.params != null
      ? result.baffle.params
      : getDefaultBaffleParams(result.baffle.type, clarinetType);

  // Chamber type
  result.chamber.type =
    result.chamber.type != null ? result.chamber.type : getDefaultChamberType();

  // Chamber params
  result.chamber.params =
    result.chamber.params != null
      ? result.chamber.params
      : getDefaultChamberParams(result.chamber.type, clarinetType);

  // Striations
  result.striations =
    result.striations != null ? result.striations : getDefaultStriations();

  // Facing
  result.facing =
    result.facing != null ? result.facing : getDefaultFacing(clarinetType);

  // Bore diameter
  result.boreDiameter =
    result.boreDiameter != null
      ? result.boreDiameter
      : getDefaultBoreDiameter(clarinetType);

  return result;
}

export function getNomenclature(mouthpiece) {
  validateMouthpiece(mouthpiece);

  const clarinetType = mouthpiece.clarinetType;
  const baffle = mouthpiece.baffle || {};
  const chamber = mouthpiece.chamber || {};
  const striations = mouthpiece.striations;
  const tipOpening = mouthpiece.tipOpening;
  const facing = mouthpiece.facing;
  const boreDiameter = mouthpiece.boreDiameter;

  // Baffle

  const formatBaffleParam = (withSign) => (param) => {
    const sign = Math.sign(param) > 0 ? "+" : "-";

    return param === 0
      ? ""
      : (withSign || sign === "-" ? sign : "") + Math.abs(param);
  };

  const baffleParams = baffle.params.filter((param) => param !== 0);
  const baffleResult =
    baffle.type +
    baffleParams.map(formatBaffleParam(baffleParams.length < 2)).join("&");

  // Chamber

  const formatChamberParam = (param) => {
    const sign = Math.sign(param) > 0 ? "+" : "-";

    return param === 0 ? "" : (sign === "-" ? sign : "") + Math.abs(param);
  };

  const chamberParams = chamber.params.filter((param) => param !== 0);
  const chamberResult =
    chamber.type + chamberParams.map(formatChamberParam).join("&");

  // Striations

  const striationsResult = striations === 0 ? "" : `x${striations}`;

  // Bore diameter

  const defaultDiameter = getDefaultBoreDiameter(clarinetType);
  const diameterModifier = boreDiameter - defaultDiameter;
  const sign = Math.sign(diameterModifier) > 0 ? "+" : "-";
  const diameterResult =
    diameterModifier === 0
      ? ""
      : ` D${sign}${Math.abs(diameterModifier).toFixed(3).replace(/0*$/, "")}`;

  return (
    baffleResult +
    chamberResult +
    striationsResult +
    " " +
    tipOpening +
    facing +
    diameterResult
  );
}
